import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  getUnixTime,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns';
import {
  DATE_RANGE_TYPES,
  dateRanges,
  getActiveDateRange,
  isNavigableRange,
} from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';

// UI-FILTROS-01 (§2.4): o período das barras de filtro, ao lado do PeriodPicker. Um período é
// `{ range, from, to }`: `range` é um nome de DATE_RANGE_TYPES, e `from` e `to` são segundos
// unix, do início ao fim do dia.

const {
  LAST_7_DAYS,
  LAST_30_DAYS,
  LAST_90_DAYS,
  THIS_WEEK,
  MONTH_TO_DATE,
  CUSTOM_RANGE,
} = DATE_RANGE_TYPES;

// Os chips de todo PeriodPicker, salvo quem passar outros.
export const PERIOD_PRESETS = [LAST_7_DAYS, LAST_30_DAYS, LAST_90_DAYS];

// Os chips dizem só os dias ("30 dias"); os outros nomes, o rótulo da lista lateral do DatePicker.
const DAYS = { [LAST_7_DAYS]: 7, [LAST_30_DAYS]: 30, [LAST_90_DAYS]: 90 };

// UF-50: os atalhos da Analytics do super admin (#16) que o DatePicker não tem. Moram só aqui, fora
// de DATE_RANGE_TYPES: o link de um relatório com `range=today` continua um nome desconhecido.
export const EXTRA_RANGE_TYPES = {
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  LAST_WEEK: 'lastWeek',
  LAST_MONTH: 'lastMonth',
};
const { TODAY, YESTERDAY, LAST_WEEK, LAST_MONTH } = EXTRA_RANGE_TYPES;

// Os dias inteiros de cada um, pelo cálculo da barra antiga (AnalyticsFilterBar). A semana passada
// é a de antes da "esta semana" do DatePicker, de segunda a domingo: a antiga ia de domingo a
// sábado, e ao lado da "esta semana" de segunda deixaria o domingo de fora, ou nas duas.
const WEEK = { weekStartsOn: 1 };
const EXTRA_DAYS = {
  [TODAY]: now => [now, now],
  [YESTERDAY]: now => [subDays(now, 1), subDays(now, 1)],
  [LAST_WEEK]: now => [
    startOfWeek(subWeeks(now, 1), WEEK),
    endOfWeek(subWeeks(now, 1), WEEK),
  ],
  [LAST_MONTH]: now => [
    startOfMonth(subMonths(now, 1)),
    endOfMonth(subMonths(now, 1)),
  ],
};
const EXTRA_LABELS = {
  [TODAY]: t => t('FILTER_BAR.PERIOD.TODAY'),
  [YESTERDAY]: t => t('FILTER_BAR.PERIOD.YESTERDAY'),
  [LAST_WEEK]: t => t('FILTER_BAR.PERIOD.LAST_WEEK'),
  [LAST_MONTH]: t => t('FILTER_BAR.PERIOD.LAST_MONTH'),
};

// Um período pelo nome, com as datas de `now`.
export const periodFromRange = (range, now = new Date()) => {
  if (EXTRA_DAYS[range]) {
    const [start, end] = EXTRA_DAYS[range](now);
    return {
      range,
      from: getUnixTime(startOfDay(start)),
      to: getUnixTime(endOfDay(end)),
    };
  }
  const { start, end } = getActiveDateRange(range, now);
  return { range, from: getUnixTime(start), to: getUnixTime(end) };
};

// "30 dias", "Últimos 6 meses" ou "Personalizado". Era o rótulo do período no resumo da página
// Atendimento (o AtendimentoFilters.vue, que saiu na fase H da REPORTS-03).
export const periodLabel = (period, t) => {
  const range = period?.range;
  if (DAYS[range]) return t('FILTER_BAR.PERIOD.DAYS', { count: DAYS[range] });
  if (EXTRA_LABELS[range]) return EXTRA_LABELS[range](t);
  const named =
    range !== CUSTOM_RANGE && dateRanges.find(item => item.value === range);
  return named ? t(named.label) : t('FILTER_BAR.PERIOD.CUSTOM');
};

const RANGE_TYPES = Object.values(DATE_RANGE_TYPES);
const EXTRA_TYPES = Object.values(EXTRA_RANGE_TYPES);

// O nome rolante anda com o dia ("últimos 30 dias"): as datas dele são sempre as de hoje. Não são
// rolantes o personalizado, cujas datas são o período, nem "esta semana" e "este mês", que o
// DatePicker leva para trás ("este mês" navegado para agosto). Os do super admin são: "ontem" é
// sempre o de hoje, e o "hoje" aberto antes da meia-noite não diz mais as datas depois dela.
export const isRollingRange = range =>
  EXTRA_TYPES.includes(range) ||
  (RANGE_TYPES.includes(range) &&
    range !== CUSTOM_RANGE &&
    !isNavigableRange(range));

// O mês e a semana inteiros que começam em `from`, como as setas do DatePicker os dão
// (`getRangeAtOffset`, com a semana de segunda a domingo).
const WHOLE = {
  [MONTH_TO_DATE]: date => [startOfMonth(date), endOfMonth(date)],
  [THIS_WEEK]: date => [
    startOfWeek(date, { weekStartsOn: 1 }),
    endOfWeek(date, { weekStartsOn: 1 }),
  ],
};
const isWholePeriod = ({ range, from, to }) => {
  const [start, end] = WHOLE[range](new Date(from * 1000));
  return (
    getUnixTime(startOfDay(start)) === from && getUnixTime(endOfDay(end)) === to
  );
};

// O nome diz as datas? O rolante, só com as de hoje para ele. Com as de outro dia (o link antigo de
// outro relatório, que grava o nome junto com as datas) "30 dias" ficaria sobre uma janela velha.
// "Esta semana" e "este mês" que começam hoje também só com o fim de hoje: "este mês" de 1 a 15,
// aberto no dia 27, diria de 1 a 27. Navegados para trás (agosto), o nome continua o daquelas
// datas, mas só do mês ou da semana inteira: "este mês" salvo no dia 15 de agosto, de 1 a 15, lido
// em setembro, diria agosto inteiro.
export const nameFitsDates = (period, now = new Date()) => {
  const range = period?.range;
  if (!isRollingRange(range) && !isNavigableRange(range)) return true;
  const today = periodFromRange(range, now);
  if (today.from === period.from) return today.to === period.to;
  return isNavigableRange(range) && isWholePeriod(period);
};

// "Esta semana" e "este mês" em curso: as datas começam nas de agora. Levados para trás pelas setas
// do DatePicker (agosto), o nome continua sendo o daquelas datas, mas não diz mais "este".
export const isOngoingRange = (period, now = new Date()) =>
  isNavigableRange(period?.range) &&
  periodFromRange(period.range, now).from === period.from;

// O chip que o PeriodPicker pressiona: o do nome, ou "Personalizado" quando o nome não diz as datas,
// e também para "este mês" e "esta semana" levados para trás (o DatePicker ao lado diz o mês ou a
// semana).
export const periodChip = (period, now = new Date()) => {
  if (!nameFitsDates(period, now)) return CUSTOM_RANGE;
  return isNavigableRange(period?.range) && !isOngoingRange(period, now)
    ? CUSTOM_RANGE
    : period?.range;
};

// O nome do período (que o seletor mostra) e as datas (que os dados usam) têm de
// concordar. Um link com só `range=last30days` mostrava "Últimos 30 dias" sobre
// os 7 dias padrão: sem from/to, o nome dá as datas. Nome que o seletor não
// conhece é descartado (ele não teria o que mostrar), "custom" sem datas volta ao
// padrão, e datas sem nome são um intervalo personalizado.
export const resolvePeriod = (range, from, to, now) => {
  const known = RANGE_TYPES.includes(range) ? range : null;
  if (from && to) {
    return { range: known || CUSTOM_RANGE, from, to };
  }
  if (!known || known === CUSTOM_RANGE) {
    return { range: null, from: null, to: null };
  }
  const { start, end } = getActiveDateRange(known, now);
  return { range: known, from: getUnixTime(start), to: getUnixTime(end) };
};

const primeiro = bruto => [bruto].flat()[0];
const numero = bruto => Number(primeiro(bruto)) || null;

// O codec de URL do período para o useFilterQuery (`{ param, read, write }`).
// - Lê pelo `resolvePeriod`, e o que não se lê fica `undefined`: vale o padrão, e o link vazio
//   não vence o filtro lembrado. O período que termina antes de começar também não se lê. O nome
//   rolante com as datas de outro dia (o link antigo de outro relatório) é lido como
//   personalizado: os dados são dessas datas, e o nome não as diz mais. "Este mês" e "esta
//   semana" que começam agora e terminam antes de hoje também: lidos pelo nome, iriam para a
//   URL sem as datas, e a releitura alargava o período até hoje. E os de trás cortados (de 1 a
//   15 de agosto), que o nome diria inteiros.
// - Escreve o nome rolante sem as datas, mesmo com as de ontem (o filtro lido antes da meia-noite
//   e escrito depois): um link de "últimos 30 dias" continua sendo dos últimos 30 dias amanhã.
//   "Esta semana" e "este mês" em curso também vão só pelo nome, porque o fim deles anda com o
//   dia; navegados para trás, levam as datas, como o personalizado. Sem as duas datas, vai só o
//   nome, que o read lê com as de hoje; o personalizado sem datas, que o read lê como o padrão,
//   não vai.
// - O padrão não vai para a URL, como nos outros campos do useFilterQuery: o link fica curto, e o
//   `hasAny` não o toma por um filtro que vence o lembrado. Ele (`fallback`) é calculado a cada
//   leitura: um objeto fixo, feito quando a tela montou, ficaria com as datas de ontem depois da
//   meia-noite.
// - `presets` é da tela sem "Personalizado" (o PeriodPicker com `allowCustom=false`), que passa
//   aqui os mesmos chips: o período cujo chip não é um deles (os 6 meses, o personalizado, "este
//   mês" de agosto) não se lê, e vale o padrão, que tem de ser um dos chips. Sem "Personalizado",
//   o seletor não tem onde mostrar outro período.
export const periodCodec = ({
  fallback = LAST_30_DAYS,
  presets = null,
} = {}) => ({
  param: ['range', 'from', 'to'],
  read: query => {
    const now = new Date();
    const period = resolvePeriod(
      primeiro(query.range),
      numero(query.from),
      numero(query.to),
      now
    );
    if (!period.range || period.from > period.to) return undefined;
    if (presets && !presets.includes(periodChip(period, now))) return undefined;
    return nameFitsDates(period, now)
      ? period
      : { ...period, range: CUSTOM_RANGE };
  },
  write: period => {
    if (!period?.range) return {};
    const { range, from, to } = period;
    const relative =
      isRollingRange(range) || isOngoingRange(period) || !(from && to);
    if (!relative) return { range, from, to };
    return range === fallback || range === CUSTOM_RANGE ? {} : { range };
  },
  get default() {
    return periodFromRange(fallback);
  },
});
